import React from 'react';
import { X, Check, Sparkles } from 'lucide-react';
import { dash, cn } from '../../theme/dashboardTheme';
import { PRICING_FOOTNOTE, CLOUD_COMING_SOON_NOTE, getTrialProgressPercent } from '../../utils/pricing';

export function PageHeader({ overline, title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 min-w-0">
      <div className="min-w-0 flex-1">
        {overline && <p className={dash.overline}>{overline}</p>}
        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1 break-words">{title}</h1>
        {subtitle && <p className="text-slate-400 mt-2 text-sm sm:text-base">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Card({ children, className, hover }) {
  return (
    <div className={cn(dash.card, dash.cardPad, hover && dash.cardHover, className)}>
      {children}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, hint, accent = 'indigo' }) {
  const accents = {
    indigo: 'from-indigo-600/20 to-violet-600/10 text-indigo-400',
    emerald: 'from-emerald-600/20 to-teal-600/10 text-emerald-400',
    sky: 'from-sky-600/20 to-cyan-600/10 text-sky-400',
    amber: 'from-amber-600/20 to-orange-600/10 text-amber-400',
  };
  return (
    <div className={cn(dash.card, dash.cardPad)}>
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <p className="text-slate-500 text-xs font-medium uppercase tracking-wide truncate">{label}</p>
          <p className="text-lg sm:text-xl font-bold text-white mt-1 truncate">{value}</p>
          {hint && <p className="text-slate-500 text-xs mt-1 truncate">{hint}</p>}
        </div>
        {Icon && (
          <div className={cn('w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br flex items-center justify-center', accents[accent])}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </div>
  );
}

export function Badge({ children, variant = 'default', className }) {
  const styles = {
    default: 'bg-slate-800 text-slate-300',
    success: 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30',
    warning: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30',
    info: 'bg-indigo-500/15 text-indigo-400 ring-1 ring-indigo-500/30',
    danger: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30',
  };
  return (
    <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full', styles[variant], className)}>
      {children}
    </span>
  );
}

export function Alert({ children, variant = 'info', action }) {
  const styles = {
    info: 'bg-indigo-500/10 border-indigo-500/30 text-slate-300',
    success: 'bg-emerald-500/10 border-emerald-500/30 text-slate-300',
    warning: 'bg-amber-500/10 border-amber-500/30 text-slate-300',
    error: 'bg-red-500/10 border-red-500/30 text-slate-300',
  };
  return (
    <div className={cn('rounded-xl border p-4 text-sm flex flex-wrap items-start justify-between gap-3', styles[variant])}>
      <div className="flex-1">{children}</div>
      {action}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <Card>
      <div className="text-center py-8">
        {Icon && (
          <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 flex items-center justify-center mx-auto mb-4">
            <Icon className="w-7 h-7 text-indigo-400" />
          </div>
        )}
        <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
        <p className="text-slate-400 text-sm max-w-md mx-auto mb-5">{description}</p>
        {action}
      </div>
    </Card>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={cn(
          'relative w-full bg-slate-900 rounded-2xl border border-slate-700/80 shadow-2xl max-h-[90vh] overflow-y-auto',
          wide ? 'max-w-lg' : 'max-w-md',
        )}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/95 backdrop-blur">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function FieldLabel({ children }) {
  return <label className={dash.label}>{children}</label>;
}

export function Input({ className, icon: Icon, ...props }) {
  return (
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
      )}
      <input className={cn(dash.input, Icon && 'pl-10', className)} {...props} />
    </div>
  );
}

export function Textarea({ className, ...props }) {
  return <textarea className={cn(dash.input, 'resize-none', className)} {...props} />;
}

export function Select({ className, children, ...props }) {
  return (
    <select className={cn(dash.input, className)} {...props}>
      {children}
    </select>
  );
}

export function TrialBanner({ trialDaysLeft, trialEndsAt, onRegister }) {
  const pct = getTrialProgressPercent(trialDaysLeft);
  const endLabel = trialEndsAt
    ? new Date(trialEndsAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'trial end';

  return (
    <div className={cn(dash.card, 'overflow-hidden p-0 border-indigo-500/30')}>
      <div className={cn(dash.heroGradient, 'p-5 sm:p-6')}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge variant="info">
                <Sparkles className="w-3 h-3 inline mr-1" />
                7-day free trial
              </Badge>
              <span className="text-slate-400 text-sm">
                {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} remaining
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              Connect your first counter PC free — branch trial active
            </h2>
            <p className="text-slate-400 text-sm mt-2 max-w-2xl leading-relaxed">
              Trial active until <strong className="text-slate-200">{endLabel}</strong>.
              After trial: ₹1,999 every year per branch.
              One counter PC per branch; mobile on LAN stays free.
            </p>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                <span>Trial progress</span>
                <span>{Math.round(pct)}% used</span>
              </div>
              <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </div>
          {onRegister && (
            <Btn onClick={onRegister} className="shrink-0 self-start lg:self-center">
              Activate free trial
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

export function PricingFootnote() {
  return (
    <div className={cn(dash.nested, 'p-4 text-center')}>
      <p className="text-slate-400 text-sm leading-relaxed">{PRICING_FOOTNOTE}</p>
      <p className="text-slate-500 text-xs mt-2">Mobile waiter app on branch LAN — always free</p>
    </div>
  );
}

export function PricingPlanCard({ plan, onAction }) {
  return (
    <div
      className={cn(
        dash.pricingCard,
        plan.highlight && dash.pricingHighlight,
        plan.disabled && 'opacity-60',
      )}
    >
      {plan.badge && (
        <span className="self-start -mt-1 mb-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-amber-400 text-amber-950">
          {plan.badge}
        </span>
      )}
      <h3 className="text-lg font-bold text-white">{plan.name}</h3>
      <div className="flex items-baseline gap-2 mt-3 mb-1">
        <span className="text-3xl font-bold text-white">{plan.price}</span>
        {plan.period && <span className="text-slate-500 text-sm">/ {plan.period}</span>}
      </div>
      <p className="text-slate-400 text-sm mb-5 flex-1 leading-relaxed">{plan.description}</p>
      <ul className="space-y-2.5 mb-6">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
            <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {plan.comingSoon ? (
        <div className="space-y-2">
          <Btn variant="secondary" className="w-full" disabled>
            {plan.cta}
          </Btn>
          <p className="text-slate-500 text-xs text-center leading-relaxed">
            <a
              href="mailto:support@homebillingpos.com?subject=Cloud%20plan%20early%20access"
              className="text-indigo-400 hover:text-indigo-300 font-medium"
            >
              Email us
            </a>
            {' '}if you need multi-branch before launch.
          </p>
        </div>
      ) : plan.contact ? (
        <Btn variant="secondary" className="w-full" onClick={() => window.location.href = 'mailto:support@homebillingpos.com'}>
          {plan.cta}
        </Btn>
      ) : (
        <Btn
          className="w-full"
          variant={plan.highlight ? 'primary' : 'secondary'}
          disabled={plan.disabled}
          onClick={onAction}
        >
          {plan.cta}
        </Btn>
      )}
    </div>
  );
}

export function CloudComingSoonAlert({ compact }) {
  return (
    <Alert variant="info">
      <div className={compact ? 'space-y-1.5' : 'space-y-1.5'}>
        <p className={cn('font-semibold text-indigo-300', compact ? 'text-sm' : 'text-sm sm:text-base')}>
          Cloud — coming soon
        </p>
        <p className={cn('text-slate-400 leading-relaxed', compact ? 'text-xs sm:text-sm' : 'text-sm')}>
          {compact
            ? 'Multi-branch roll-up analytics are not available yet. Standard plan = one branch per account.'
            : CLOUD_COMING_SOON_NOTE}
        </p>
      </div>
    </Alert>
  );
}

export function StepIndicator({ steps, current }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-6">
      {steps.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all',
                  done && dash.stepDone,
                  active && dash.stepActive,
                  !done && !active && dash.stepIdle,
                )}
              >
                {done ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={cn('text-[0.65rem] font-medium text-center truncate w-full', active ? 'text-white' : 'text-slate-500')}>
                {step}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={cn('h-0.5 flex-1 rounded mb-5', done ? 'bg-emerald-500/40' : 'bg-slate-800')} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function QuickActionCard({ icon: Icon, title, description, actionLabel, onClick, accent = 'indigo' }) {
  const accents = {
    indigo: 'from-indigo-600/25 to-violet-600/10 text-indigo-400',
    emerald: 'from-emerald-600/25 to-teal-600/10 text-emerald-400',
    sky: 'from-sky-600/25 to-cyan-600/10 text-sky-400',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(dash.card, dash.cardPad, dash.cardHover, 'text-left w-full group')}
    >
      <div className={cn('w-11 h-11 rounded-xl bg-gradient-to-br flex items-center justify-center mb-4', accents[accent])}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-white font-semibold mb-1 group-hover:text-indigo-200 transition-colors">{title}</h3>
      <p className="text-slate-400 text-sm mb-4 leading-relaxed">{description}</p>
      <span className={dash.btnGhost}>{actionLabel} →</span>
    </button>
  );
}

export function Btn({ variant = 'primary', className, children, type = 'button', ...props }) {
  const variants = {
    primary: dash.btnPrimary,
    secondary: dash.btnSecondary,
    ghost: dash.btnGhost,
    danger: dash.btnDanger,
    success: 'inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl transition-colors disabled:opacity-50',
  };
  return (
    <button type={type} className={cn(variants[variant], className)} {...props}>
      {children}
    </button>
  );
}

export { dash, cn };